import { FC } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { UsageBo } from '@utils/api/back-office/usage/use-get-usage-bo.tsx';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

interface PieDataItem {
  name: string;
  value: number;
}

interface CostDistributionChartProps {
  data: UsageBo[];
}

interface CustomTooltipPayload {
  value: number;
  name: string;
}

const CostDistributionChart: FC<CostDistributionChartProps> = ({ data }) => {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const sortedData = [...data].sort(
    (a, b) => parseFloat(b.total_cost) - parseFloat(a.total_cost)
  );

  const topOrgs = sortedData.slice(0, 5);
  const othersCost = sortedData
    .slice(5)
    .reduce((sum: number, org: UsageBo) => sum + parseFloat(org.total_cost), 0);

  const pieData: PieDataItem[] = topOrgs.map(org => ({
    name: org.organization_name,
    value: parseFloat(parseFloat(org.total_cost).toFixed(2))
  }));

  if (othersCost > 0) {
    pieData.push({
      name: t('others'),
      value: parseFloat(othersCost.toFixed(2))
    });
  }

  const totalCost = pieData.reduce((sum, item) => sum + item.value, 0);

  /* eslint-disable no-restricted-syntax -- Semantic chart palette */
  const COLORS = [
    '#3b82f6', // blue-500
    '#60a5fa', // blue-400
    '#93c5fd', // blue-300
    '#2563eb', // blue-600
    '#1d4ed8', // blue-700
    '#7dd3fc' // sky-300
  ];
  /* eslint-enable no-restricted-syntax */

  const customTooltip: (
    props: TooltipProps<number, string>
  ) => React.ReactNode = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0] as CustomTooltipPayload;
      const percentage = ((data.value / totalCost) * 100).toFixed(1);
      return (
        <div
          className="p-2 border border-hairline bg-app-soft rounded-2xl shadow-pop"
          style={{ color: getColour('normalText') }}
        >
          <p className="font-semibold">{data.name}</p>
          <p>
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'EUR',
              minimumFractionDigits: 2
            }).format(data.value)}
          </p>
          <p>
            {percentage}
            {t('pct-of-total')}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <h2 className="text-base font-semibold mb-4">{t('cost-distribution')}</h2>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%" minHeight={350}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) =>
                `${name}: ${(percent * 100).toFixed(1)}%`
              }
              outerRadius="70%"
              // eslint-disable-next-line no-restricted-syntax -- Default chart fill, overridden per Cell below
              fill="#8884d8"
              dataKey="value"
            >
              {pieData.map((_, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={customTooltip} />
            <Legend
              wrapperStyle={{ paddingTop: '10px' }}
              iconType="circle"
              iconSize={10}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CostDistributionChart;
